import { useRetailerCoreWithSelector } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/RetailerCoreProvider";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { createContext, useContext, useMemo } from "react";
import { createStore, StoreApi, useStore } from "zustand";

interface RetailerInsider {
  isInsider: boolean;
  isEligibleForInsider: boolean;
  shouldShowInsider: boolean;
  insiderMembershipState: keyof typeof IRetailer.InsiderMembershipState;
  shouldShowInsiderSignupIncentive: boolean;
}

const RetailerInsiderContext = createContext<
  StoreApi<RetailerInsider> | undefined
>(undefined);

export const RetailerInsiderProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const initialRetailer = getRetailer();
  const retailer = useRetailerCoreWithSelector((state) => state.retailer);

  const store = useMemo(
    () =>
      createStore<RetailerInsider>(() => {
        const insiderMembershipState =
          retailer?.insider_membership_state ??
          initialRetailer?.insider_membership_state ??
          IRetailer.InsiderMembershipState.NOT_ELIGIBLE;

        const isInsider =
          insiderMembershipState === IRetailer.InsiderMembershipState.ACTIVE;
        const isEligibleForInsider =
          insiderMembershipState ===
            IRetailer.InsiderMembershipState.ELIGIBLE ||
          insiderMembershipState === IRetailer.InsiderMembershipState.CHURNED;
        const shouldShowInsider =
          insiderMembershipState !==
          IRetailer.InsiderMembershipState.NOT_ELIGIBLE;

        return {
          isInsider,
          isEligibleForInsider,
          shouldShowInsider,
          insiderMembershipState,
          shouldShowInsiderSignupIncentive: !!(
            retailer?.show_insider_signup_offer ??
            initialRetailer?.show_insider_signup_offer
          ),
        };
      }),
    [
      retailer?.insider_membership_state,
      initialRetailer?.insider_membership_state,
      retailer?.show_insider_signup_offer,
      initialRetailer?.show_insider_signup_offer,
    ]
  );

  return (
    <RetailerInsiderContext.Provider value={store}>
      {children}
    </RetailerInsiderContext.Provider>
  );
};

export const useInsider = () => {
  const context = useContext(RetailerInsiderContext);
  if (!context) {
    throw new Error(
      "RetailerInsiderContext not found. Must be used within RetailerInsiderProvider"
    );
  }
  return useStore(context);
};
